/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  SVGIcon,
  theme,
  useThemeSwitcher,
  THEME_OPTIONS,
} from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { useAuth0 } from "~/components/Auth/auth";
import { ACTIONS, Can, ROUTES } from "~/routing";

export const DropItem = styled.li`
  display: flex;
  align-items: center;
  justify-items: center;
  padding: 0.5rem 0.2rem;
  font-size: 16px;
  cursor: pointer;
  :hover {
    opacity: 0.7;
  }
  &:last-of-type {
    border-top: 1px solid ${theme.color.border.border01};
  }
`;

export const Email = styled.div`
  font-size: 0.7rem;
  font-weight: bold;
  padding-bottom: 0.2rem;
  border-bottom: 1px solid ${theme.color.border.border01};
  max-width: 100%;
  word-wrap: break-word;
`;

export const DropBtn = styled.button`
  flex: 1;
  padding: 0;
  background: none;
  display: flex;
  align-items: center;
  justify-items: center;
  border: none;
  color: ${theme.color.text.text01};
  cursor: pointer;
  font-size: 0.85rem;
  &:hover {
    text-decoration: none;
    color: ${theme.color.text.text01};
  }
`;

export const DropList = styled.ul`
  border-radius: 4px;
  padding: 0.5rem;
  margin-bottom: 0;
`;

export const IconWrap = styled.div`
  display: flex;
  margin-right: 1rem;
`;

export const DropdownWrapStyle = styled.div`
  position: relative;
  cursor: pointer;
`;

export const BoxWrap = styled.div`
  min-width: 0;
  padding: 0.55rem 1.25rem 0.55rem 1.25rem;
  color: ${theme.color.text.text01};
  background: ${theme.color.background.ui01};
  border-radius: 4px;
  transition: box-shadow 0.2s ease-in-out;
  ${/* sc-selector */ DropdownWrapStyle}:hover & {
    box-shadow: ${theme.shadow.shadow04};
  }
`;

export const ShowDropDown = styled.div`
  background: ${theme.color.background.ui01};
  transition: all 0.2s ease-in-out;
  position: absolute;
  opacity: 0;
  visibility: hidden;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
  top: 100%;
  left: 0;
  width: 100%;
  z-index: 100;

  ${/* sc-selector */ DropdownWrapStyle}:hover & {
    opacity: 1;
    visibility: visible;
    box-shadow: ${theme.shadow.shadow04};
  }
`;

export const ArrowWrap = styled.div`
  display: block;
  transition: transform 0.2s ease-in-out;
  ${/* sc-selector */ DropdownWrapStyle}:hover & {
    transform: rotate(180deg);
  }
`;

type UserDropdownProps = {
  email: string;
};

function UserDropdown({ email }: UserDropdownProps) {
  const { logout } = useAuth0();
  const { currentTheme, setTheme } = useThemeSwitcher();
  const nextTheme =
    currentTheme === THEME_OPTIONS.LIGHT
      ? THEME_OPTIONS.DARK
      : THEME_OPTIONS.LIGHT;

  return (
    <DropList>
      <Email>{email}</Email>
      <DropItem>
        <DropBtn onClick={() => setTheme(nextTheme)}>
          <IconWrap>
            <SVGIcon name="dashboard" />
          </IconWrap>
          Switch to {THEME_OPTIONS[nextTheme].toLowerCase()} mode
        </DropBtn>
      </DropItem>

      <DropItem>
        <DropBtn as={Link} to={ROUTES.docs}>
          <IconWrap>
            <SVGIcon name="api" />
          </IconWrap>
          API documentation
        </DropBtn>
      </DropItem>

      <Can action={ACTIONS.VISIT_GLOBAL_SETTINGS}>
        <DropItem>
          <DropBtn as={Link} to={ROUTES.globalSettings}>
            <IconWrap>
              <SVGIcon name="settings" />
            </IconWrap>
            Global Settings
          </DropBtn>
        </DropItem>
      </Can>

      <Can action={ACTIONS.SEARCH_AUDIT_LOGS}>
        <DropItem>
          <DropBtn as={Link} to={ROUTES.audit}>
            <IconWrap>
              <SVGIcon name="search" />
            </IconWrap>
            Audit Logs
          </DropBtn>
        </DropItem>
      </Can>

      <DropItem>
        <DropBtn
          onClick={() => {
            logout({
              returnTo: window.location.origin,
            });
          }}
        >
          <IconWrap>
            <SVGIcon name="logout" />
          </IconWrap>
          Logout
        </DropBtn>
      </DropItem>
    </DropList>
  );
}

type DropdownWrapProps = {
  children?: React.ReactNode;
  email: string;
};

function DropdownWrap({ children, email }: DropdownWrapProps) {
  return (
    <DropdownWrapStyle>
      <BoxWrap>
        <FlexContainer>
          {children}
          <ArrowWrap>
            <SVGIcon name="down" />
          </ArrowWrap>
        </FlexContainer>
        <ShowDropDown>
          <UserDropdown email={email} />
        </ShowDropDown>
      </BoxWrap>
    </DropdownWrapStyle>
  );
}

export { DropdownWrap };
